<template>
  <div class="upload-page">
    <main class="upload-container">
      <section class="upload-intro">
        <span class="page-kicker">你的阅读空间</span>
        <h1>添加内容</h1>
        <p>上传 TXT、EPUB 或 PDF，或从公开下载链接导入电子书。我们会把内容整理到你的书架。</p>
        <ul class="format-list">
          <li><nricon name="document" /><span><strong>TXT</strong><small>自动检测编码与章节</small></span></li>
          <li><nricon name="collection" /><span><strong>EPUB</strong><small>按目录顺序提取正文</small></span></li>
          <li><nricon name="document" /><span><strong>PDF</strong><small>保留原文档，按页阅读</small></span></li>
          <li><nricon name="headphones" /><span><strong>有声书</strong><small>多段音频可组合并调整顺序</small></span></li>
          <li><nricon name="lock" /><span><strong>由你决定</strong><small>私有收藏或公开分享</small></span></li>
        </ul>
      </section>
      <div class="upload-box">
      <!-- 可见性选择（对本地上传与链接导入均生效） -->
      <div class="upload-section visibility-section">
        <h3>可见性</h3>
        <div class="nr-radios">
          <button type="button" class="nr-radio" :class="{ active: visibility === 'PRIVATE' }" @click="visibility = 'PRIVATE'"><nricon name="lock" /> 私有（仅自己可见）</button>
          <button type="button" class="nr-radio" :class="{ active: visibility === 'PUBLIC' }" @click="visibility = 'PUBLIC'"><nricon name="view" /> 公开（所有人可读）</button>
        </div>
      </div>

      <!-- 分类（可选，本地上传时应用） -->
      <div class="upload-section">
        <h3>分类 <small>（选填）</small></h3>
        <select v-model="category" class="nr-select">
          <option value="">不设置</option>
          <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
        </select>
      </div>

      <!-- 本地文件上传 -->
      <div class="upload-section">
        <h3>本地文件上传</h3>
        <input
          type="file"
          accept=".txt,.epub,.pdf"
          multiple
          @change="handleFileUpload"
          ref="fileInput"
          style="display: none"
        >
      <button
        type="button"
        class="upload-area"
        :class="{ dragging: isDragging, disabled: uploading }"
        :disabled="uploading"
          @click="triggerUpload"
          @dragenter.prevent="onDragEnter"
          @dragover.prevent="onDragOver"
          @dragleave="onDragLeave"
          @drop.prevent="onDrop"
        >
          <span class="upload-icon"><nricon name="upload" /></span>
          <strong>拖拽文件到这里</strong>
          <p>或点击选择 TXT / EPUB / PDF，可一次选择多本</p>
        </button>
      </div>

      <div class="upload-section audio-upload-section">
        <h3>添加有声书音轨</h3>
        <input ref="audioInput" type="file" accept=".mp3,.m4a,.m4b,.aac,.ogg,.opus,.wav,.flac,audio/*" multiple style="display:none" @change="handleAudioFiles">
        <button class="audio-pick" type="button" :disabled="uploading" @click="$refs.audioInput.click()"><nricon name="headphones" :size="18" /> 选择音频文件</button>
        <p class="audio-help">支持 MP3、M4A、M4B、AAC、OGG、Opus、WAV、FLAC。默认组合为一本有声书。</p>
        <div v-if="selectedAudioTracks.length" class="selected-audio">
          <label class="audio-separate-option"><input v-model="audioSeparate" type="checkbox"><span><strong>每条音轨单独创建一本有声书</strong><small>适合一次选择多本独立的有声内容</small></span></label>
          <label v-if="!audioSeparate" class="audio-title-field"><span>有声书名称</span><input v-model.trim="audioBookTitle" class="nr-input" maxlength="240" placeholder="输入有声书名称"></label>
          <div v-for="(track, index) in selectedAudioTracks" :key="track.id" class="selected-track">
            <span class="track-order">{{ index + 1 }}</span>
            <input v-model.trim="track.title" class="nr-input" :aria-label="`第 ${index + 1} 条音轨名称`">
            <small>{{ track.durationSeconds ? formatDuration(track.durationSeconds) : '时长未知' }}</small>
            <button type="button" :disabled="index === 0" :aria-label="`音轨上移：${track.title}`" @click="moveSelectedTrack(index, -1)"><nricon name="arrow-up" :size="14" /></button>
            <button type="button" :disabled="index === selectedAudioTracks.length - 1" :aria-label="`音轨下移：${track.title}`" @click="moveSelectedTrack(index, 1)"><nricon name="arrow-down" :size="14" /></button>
            <button class="remove-track" type="button" :aria-label="`移除音轨：${track.title}`" @click="removeSelectedTrack(index)"><nricon name="close" :size="14" /></button>
          </div>
          <div class="audio-submit-row"><span>{{ audioSeparate ? `分别创建 ${selectedAudioTracks.length} 本` : `${selectedAudioTracks.length} 条音轨 · 按当前顺序播放` }}</span><button class="btn btn--primary btn--sm" type="button" :disabled="uploading" @click="submitAudioBook">{{ audioSeparate ? '分别添加' : '添加有声书' }}</button></div>
        </div>
      </div>

      <!-- 链接导入 -->
      <div class="upload-section">
        <h3>从链接导入</h3>
        <div class="url-input">
          <label class="url-field">
            <span class="sr-only">小说下载链接</span>
            <input
            v-model="downloadUrl"
            class="nr-input"
            placeholder="请输入小说下载链接"
            @keyup.enter="handleUrlDownload"
            >
          </label>
          <button class="btn btn--primary" :disabled="downloading" @click="handleUrlDownload">
            <nricon v-if="downloading" name="loading" />
            <span v-else>导入</span>
          </button>
        </div>
        <div v-if="urlImportedBook && urlImportedBook.id" class="url-success">
          <span><nricon name="success" :size="18" /> 已添加《{{ urlImportedBook.title }}》</span>
          <button class="text-action" type="button" @click="openAdded(urlImportedBook)">打开内容 <nricon name="arrow-right" :size="15" /></button>
        </div>
      </div>
      
      <section v-if="uploadQueue.length" class="upload-queue" aria-live="polite" aria-label="上传队列">
        <div class="queue-heading"><h3>本次添加</h3><span>{{ queueSuccessCount }}/{{ uploadQueue.length }} 完成</span></div>
        <article v-for="item in uploadQueue" :key="item.id" class="queue-item" :class="'is-' + item.state">
          <span class="queue-icon"><nricon :name="queueIcon(item.state)" :size="18" /></span>
          <div class="queue-info"><strong>{{ item.name }}</strong><span>{{ queueStatus(item) }}</span>
            <div v-if="item.state === 'uploading'" class="queue-progress"><i :style="{ width: item.progress + '%' }"></i></div>
          </div>
          <button v-if="item.state === 'error'" class="queue-action" type="button" @click="retryUpload(item)">重试</button>
          <button v-if="item.state === 'success' && item.book && item.book.id" class="queue-action" type="button" @click="openAdded(item.book)">打开</button>
        </article>
        <div v-if="uploadQueue.some(item => item.state === 'error')" class="queue-note">成功添加的内容已保留，可以单独重试失败项。</div>
        <div v-if="queueSuccessCount" class="queue-footer">
          <router-link class="btn btn--primary btn--sm" to="/library">返回书库</router-link>
          <button class="btn btn--sm" type="button" @click="clearCompleted">清除已完成</button>
        </div>
      </section>
      <div v-if="downloading" class="upload-progress" role="status">
        <p class="batch-hint">正在导入链接内容</p>
        <div class="nr-progress"><span :style="{ width: uploadProgress + '%' }"></span></div>
        <div class="nr-progress-pct">{{ uploadProgress }}%</div>
      </div>
      </div>
    </main>
  </div>
</template>

<script>
import api from '@/api'
import { CATEGORIES } from '@/utils/categories'

export default {
  name: 'UploadPage',
  data() {
    return {
      uploading: false,
      downloading: false,
      uploadProgress: 0,
      downloadUrl: '',
      isDragging: false,
      visibility: 'PRIVATE',
      category: '',
      categories: CATEGORIES,
      batchTotal: 0,
      batchCurrent: 0,
      currentFileName: '',
      uploadQueue: [],
      urlImportedBook: null,
      selectedAudioTracks: [],
      audioBookTitle: '',
      audioSeparate: false
    }
  },
  computed: {
    queueSuccessCount() { return this.uploadQueue.filter(item => item.state === 'success').length }
  },
  mounted() {
    this._globalDragPrevent = (e) => { e.preventDefault() }
    document.addEventListener('dragover', this._globalDragPrevent)
    document.addEventListener('drop', this._globalDragPrevent)
  },
  beforeDestroy() {
    document.removeEventListener('dragover', this._globalDragPrevent)
    document.removeEventListener('drop', this._globalDragPrevent)
    this._globalDragPrevent = null
  },
  methods: {
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    async handleFileUpload(e) {
      const files = Array.from(e.target.files || [])
      if (!files.length) return
      await this.uploadFiles(files)
      // 清空以便再次选择相同文件也能触发 change
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    async handleAudioFiles(event) {
      const files = Array.from(event.target.files || [])
      if (this.$refs.audioInput) this.$refs.audioInput.value = ''
      if (!files.length) return
      const accepted = files.filter(file => /\.(mp3|m4a|m4b|aac|ogg|opus|wav|flac)$/i.test(file.name || ''))
      if (accepted.length !== files.length) this.$message.warning('部分文件格式不支持，已跳过')
      const inspected = await Promise.all(accepted.map(async (file, index) => ({
        id: `${Date.now()}-${index}-${file.name}`,
        file,
        title: (file.name || '').replace(/\.[^.]+$/, ''),
        durationSeconds: await this.readAudioDuration(file)
      })))
      if (!inspected.length) return
      this.selectedAudioTracks = this.selectedAudioTracks.concat(inspected)
      if (!this.audioBookTitle) this.audioBookTitle = inspected[0].title
    },
    readAudioDuration(file) {
      return new Promise(resolve => {
        const audio = document.createElement('audio')
        const objectUrl = URL.createObjectURL(file)
        const finish = duration => { URL.revokeObjectURL(objectUrl); audio.removeAttribute('src'); resolve(duration) }
        const timeout = setTimeout(() => finish(null), 8000)
        audio.preload = 'metadata'
        audio.onloadedmetadata = () => { clearTimeout(timeout); finish(Number.isFinite(audio.duration) ? audio.duration : null) }
        audio.onerror = () => { clearTimeout(timeout); finish(null) }
        audio.src = objectUrl
      })
    },
    moveSelectedTrack(index, delta) {
      const target = index + delta
      if (target < 0 || target >= this.selectedAudioTracks.length) return
      const tracks = this.selectedAudioTracks.slice()
      const [item] = tracks.splice(index, 1)
      tracks.splice(target, 0, item)
      this.selectedAudioTracks = tracks
    },
    removeSelectedTrack(index) {
      this.selectedAudioTracks.splice(index, 1)
      if (!this.selectedAudioTracks.length) this.audioBookTitle = ''
    },
    formatDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return hours ? `${hours}:${String(minutes).padStart(2, '0')}` : `${minutes} 分钟`
    },
    submitAudioBook() {
      if (!this.selectedAudioTracks.length) return
      const tracks = this.selectedAudioTracks.slice()
      const common = { visibility: this.visibility, category: this.category }
      if (this.audioSeparate) {
        tracks.forEach((track, index) => {
          const title = track.title || '有声书'
          this.uploadQueue.push({
            id: `${Date.now()}-audio-${index}-${title}`, type: 'audio', name: title,
            tracks: [track], ...common, progress: 0, state: 'pending', error: '', book: null
          })
        })
      } else {
        const title = this.audioBookTitle || tracks[0].title || '有声书'
        this.uploadQueue.push({
          id: `${Date.now()}-audio-${title}`, type: 'audio', name: title,
          tracks, ...common, progress: 0, state: 'pending', error: '', book: null
        })
      }
      this.selectedAudioTracks = []
      this.audioBookTitle = ''
      this.audioSeparate = false
      this.processQueue()
    },
    async handleUrlDownload() {
      if (!this.downloadUrl) {
        this.$message.warning('请输入下载链接')
        return
      }

      if (!this.isValidUrl(this.downloadUrl)) {
        this.$message.warning('请输入有效的URL')
        return
      }

      this.downloading = true
      this.uploadProgress = 0
      this.urlImportedBook = null

      try {
        const { data } = await api.book.downloadBook(this.downloadUrl, (progressEvent) => {
          this.uploadProgress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
        }, this.visibility)
        this.urlImportedBook = data
        this.$message.success('导入成功，可以打开内容或返回书库')
      } catch (error) {
        console.error('导入失败:', error)
        this.$message.error(error.response?.data?.message || '导入失败，请重试')
      } finally {
        this.downloading = false
        this.uploadProgress = 0
      }
    },
    isValidUrl(string) {
      try {
        new URL(string)
        return true
      } catch (_) {
        return false
      }
    },
    onDragEnter() {
      this.isDragging = true
    },
    onDragOver() {
      this.isDragging = true
    },
    onDragLeave() {
      this.isDragging = false
    },
    async onDrop(e) {
      this.isDragging = false
      const files = e.dataTransfer && e.dataTransfer.files
      if (!files || !files.length) return
      await this.uploadFiles(Array.from(files))
    },
    // 批量依次上传；每个文件独立保留状态，失败项可单独重试。
    async uploadFiles(files) {
      const isSupported = f => {
        const n = (f.name || '').toLowerCase()
        return n.endsWith('.txt') || n.endsWith('.epub') || n.endsWith('.pdf')
      }
      const txts = files.filter(isSupported)
      if (!txts.length) {
        this.$message.warning('仅支持 TXT / EPUB / PDF 文件')
        return
      }
      this.uploadQueue = this.uploadQueue.concat(txts.map((file, index) => ({
        id: `${Date.now()}-${index}-${file.name}`, file, name: file.name, visibility: this.visibility, category: this.category, state: 'pending', progress: 0, error: '', book: null
      })))
      await this.processQueue()
    },
    async processQueue() {
      if (this.uploading) return
      this.uploading = true
      for (const item of this.uploadQueue) {
        if (item.state !== 'pending') continue
        item.state = 'uploading'
        item.progress = 0
        const formData = new FormData()
        try {
          if (item.type === 'audio') {
            formData.append('title', item.name)
            formData.append('visibility', item.visibility != null ? item.visibility : this.visibility)
            const category = item.category != null ? item.category : this.category
            if (category) formData.append('category', category)
            item.tracks.forEach(track => {
              formData.append('files', track.file)
              formData.append('trackTitles', track.title)
              formData.append('durations', track.durationSeconds == null ? '' : String(track.durationSeconds))
            })
          } else {
            formData.append('file', item.file)
          }
          const onProgress = (event) => {
            if (!event.total) return
            item.progress = Math.min(100, Math.round((event.loaded * 100) / event.total))
            if (item.progress >= 100) item.state = 'processing'
          }
          const { data } = item.type === 'audio'
            ? await api.book.uploadAudioBook(formData, onProgress)
            : await api.book.uploadBook(formData, onProgress, item.visibility != null ? item.visibility : this.visibility, item.category != null ? item.category : this.category)
          item.book = data
          item.state = 'success'
          item.progress = 100
        } catch (error) {
          item.state = 'error'
          item.error = (error.response && error.response.data && error.response.data.message) || '上传或解析失败'
        }
      }
      this.uploading = false
      if (this.queueSuccessCount) this.$message.success(`已添加 ${this.queueSuccessCount} 项内容`)
    },
    retryUpload(item) {
      item.state = 'pending'
      item.error = ''
      item.progress = 0
      this.processQueue()
    },
    openAdded(book) {
      if (book && book.id) this.$router.push({ name: 'BookDetail', params: { id: String(book.id) } })
    },
    clearCompleted() {
      this.uploadQueue = this.uploadQueue.filter(item => item.state !== 'success')
    },
    queueIcon(state) {
      return ({ pending: 'time', uploading: 'upload', processing: 'loading', success: 'success', error: 'error' })[state] || 'document'
    },
    queueStatus(item) {
      if (item.state === 'pending') return '等待上传'
      if (item.state === 'uploading') return `正在上传 ${item.progress}%`
      if (item.state === 'processing') return '上传完成，正在解析'
      if (item.state === 'success') return '添加完成'
      return item.error || '上传失败'
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 120px);
  padding: 20px;
}

.upload-box {
  width: 100%;
  max-width: 500px;
  padding: 30px;
  background: var(--nr-surface);
  border: 1px solid var(--nr-border);
  border-radius: var(--nr-radius-lg);
  box-shadow: var(--nr-shadow);
}

.upload-section {
  margin-bottom: 28px;

  h3 {
    margin-bottom: 14px;
    font-size: 15px;
    font-weight: 500;
    color: var(--nr-text);
  }
}

.upload-area {
  border: 1.5px dashed var(--nr-border);
  border-radius: var(--nr-radius);
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover { border-color: var(--nr-accent); background: var(--nr-accent-bg); }

  i { font-size: 44px; color: var(--nr-text-3); }
  p { margin-top: 10px; color: var(--nr-text-2); font-size: 14px; }
}

.upload-area.dragging {
  border-color: var(--nr-accent);
  background: var(--nr-accent-bg);
}

.url-input {
  margin-top: 12px;
  display: flex;
  gap: 8px;
  .nr-input { flex: 1; }
  .btn { flex-shrink: 0; }
}

.upload-progress {
  margin-top: 20px;

  .batch-hint { font-size: 13px; color: var(--nr-text-2); margin-bottom: 8px; }
  .nr-progress {
    height: 8px;
    background: var(--nr-surface-2);
    border-radius: 4px;
    overflow: hidden;
    span { display: block; height: 100%; background: var(--nr-accent); border-radius: 4px; transition: width 0.2s ease; }
  }
  .nr-progress-pct { margin-top: 6px; font-size: 12px; color: var(--nr-text-3); text-align: right; }
}
</style> 

<style lang="scss" scoped>
.upload-page { min-height: 100vh; }

.page-top {
  position: sticky;
  top: 0;
  z-index: 20;
  border-bottom: 1px solid color-mix(in srgb, var(--nr-border) 80%, transparent);
  background: color-mix(in srgb, var(--nr-surface) 90%, transparent);
  backdrop-filter: blur(18px);
}

.page-top-inner {
  display: flex;
  width: min(100% - 40px, var(--nr-content-width));
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  margin-inline: auto;
}

.back,
.mini-brand {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 7px;
  padding: 0;
  color: var(--nr-text-2);
  border: 0;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.back:hover { color: var(--nr-accent-text); }
.mini-brand { color: var(--nr-text); font-family: var(--nr-font-display); font-size: 18px; }
.mini-brand i { display: grid; width: 31px; height: 31px; place-items: center; color: var(--nr-on-accent); border-radius: 50% 50% 50% 10px; background: var(--nr-accent); font-family: var(--nr-font-body); font-size: 16px; }

.upload-container {
  display: grid;
  width: min(100% - 40px, 1060px);
  min-height: auto;
  grid-template-columns: minmax(0, .78fr) minmax(460px, 1.22fr);
  align-items: start;
  gap: clamp(40px, 8vw, 90px);
  margin-inline: auto;
  padding: 64px 0 80px;
}

.upload-intro {
  position: sticky;
  top: 130px;
  padding-top: 12px;

  h1 {
    margin: 0 0 16px;
    font-family: var(--nr-font-display);
    font-size: clamp(30px, 4vw, 38px);
    font-weight: 600;
    line-height: 1.15;
    letter-spacing: -.02em;
  }

  > p { margin: 0; color: var(--nr-text-2); font-size: 15px; line-height: 1.8; }
}
.page-kicker { display: block; margin-bottom: 8px; color: var(--nr-accent); font-size: 12px; font-weight: 700; letter-spacing: .07em; }

.format-list {
  display: grid;
  gap: 13px;
  margin: 34px 0 0;
  padding: 0;
  list-style: none;

  li { display: flex; align-items: center; gap: 12px; }
  > li > i { display: grid; width: 36px; height: 36px; flex-shrink: 0; place-items: center; color: var(--nr-accent-text); border: 1px solid var(--nr-border); border-radius: var(--nr-radius-sm); background: var(--nr-surface); }
  span { display: flex; flex-direction: column; }
  strong { color: var(--nr-text); font-size: 13px; font-weight: 600; }
  small { color: var(--nr-text-3); font-size: 11px; }
}

.upload-box {
  max-width: none;
  padding: clamp(24px, 4vw, 36px);
  border-radius: var(--nr-radius-lg);
  box-shadow: var(--nr-shadow);
}

.upload-section {
  margin-bottom: 28px;
  padding-bottom: 28px;
  border-bottom: 1px solid var(--nr-border);

  &:last-of-type { margin-bottom: 0; padding-bottom: 0; border-bottom: 0; }
  h3 { margin-bottom: 13px; font-size: 16px; font-weight: 600; }
  h3 small { color: var(--nr-text-3); font-size: 12px; font-weight: 400; }
}

.upload-area {
  display: flex;
  width: 100%;
  min-height: 180px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  color: var(--nr-text);
  border: 1.5px dashed var(--nr-border-strong);
  border-radius: var(--nr-radius-lg);
  background: var(--nr-surface-2);
  transition: border-color 180ms ease, background 180ms ease;

  .upload-icon { display: grid; width: 50px; height: 50px; margin-bottom: 14px; place-items: center; color: var(--nr-accent-text); border-radius: 50%; background: var(--nr-accent-bg); }
  i { color: inherit; font-size: 23px; }
  strong { font-size: 16px; font-weight: 600; }
  p { margin-top: 4px; color: var(--nr-text-3); font-size: 13px; }

  &:hover,
  &.dragging { border-color: var(--nr-accent); background: var(--nr-accent-bg); }
  &.disabled { opacity: .58; cursor: wait; }
}

.url-field { min-width: 0; flex: 1; }
.url-input { margin-top: 0; gap: 10px; }
.url-input .btn { min-width: 78px; }

.upload-progress {
  margin-top: 30px;
  padding: 18px;
  border-radius: var(--nr-radius);
  background: var(--nr-accent-bg);

  .nr-progress { height: 5px; background: color-mix(in srgb, var(--nr-accent) 20%, transparent); }
}

.url-success { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 12px; padding: 10px 12px; color: var(--nr-success); border-radius: 9px; background: var(--nr-success-bg); font-size: 12px; }
.url-success > span { display: flex; min-width: 0; align-items: center; gap: 7px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.text-action { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 5px; color: var(--nr-accent-text); border: 0; background: transparent; font-size: 12px; font-weight: 650; cursor: pointer; }
.upload-queue { margin-top: 22px; padding-top: 21px; border-top: 1px solid var(--nr-border); }
.queue-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 11px; h3 { margin: 0; color: var(--nr-text); font-size: 15px; } span { color: var(--nr-text-3); font-size: 11px; } }
.queue-item { display: flex; min-width: 0; align-items: center; gap: 10px; padding: 11px 0; border-top: 1px solid var(--nr-border); }
.queue-icon { display: grid; width: 34px; height: 34px; flex: 0 0 34px; place-items: center; color: var(--nr-text-2); border-radius: 9px; background: var(--nr-surface-2); }
.queue-info { display: grid; min-width: 0; flex: 1; gap: 3px; }
.queue-info strong { overflow: hidden; color: var(--nr-text); font-size: 12px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.queue-info > span { color: var(--nr-text-3); font-size: 11px; }
.queue-item.is-success .queue-icon { color: var(--nr-success); background: var(--nr-success-bg); }
.queue-item.is-error .queue-icon, .queue-item.is-error .queue-info > span { color: var(--nr-danger); }
.queue-progress { height: 3px; overflow: hidden; margin-top: 3px; border-radius: 99px; background: var(--nr-surface-2); i { display: block; height: 100%; border-radius: inherit; background: var(--nr-accent); transition: width 120ms ease; } }
.queue-action { min-height: 36px; padding: 0 10px; color: var(--nr-accent-text); border: 1px solid var(--nr-border); border-radius: 8px; background: var(--nr-surface); font-size: 11px; font-weight: 650; cursor: pointer; }
.queue-note { padding: 10px; color: var(--nr-warning); border-radius: 8px; background: var(--nr-warning-bg); font-size: 11px; line-height: 1.5; }
.queue-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.audio-pick { display: inline-flex; min-height: 42px; align-items: center; gap: 8px; padding: 0 14px; color: var(--nr-accent-text); border: 1px solid var(--nr-border); border-radius: 10px; background: var(--nr-surface); font-size: 13px; font-weight: 600; cursor: pointer; &:hover:not(:disabled) { border-color: var(--nr-accent); background: var(--nr-accent-bg); } &:disabled { opacity: .55; cursor: wait; } }
.audio-help { margin: 8px 0 14px; color: var(--nr-text-3); font-size: 11px; line-height: 1.55; }
.selected-audio { display: grid; gap: 10px; margin-top: 14px; padding: 13px; border: 1px solid var(--nr-border); border-radius: 11px; background: var(--nr-bg); }
.audio-separate-option { display: flex; align-items: flex-start; gap: 9px; padding: 4px 1px; cursor: pointer; input { width: 16px; height: 16px; flex: 0 0 16px; margin-top: 2px; accent-color: var(--nr-accent); } span { display: grid; gap: 3px; } strong { color: var(--nr-text); font-size: 11px; font-weight: 650; } small { color: var(--nr-text-3); font-size: 10px; } }
.audio-title-field { display: grid; gap: 6px; color: var(--nr-text-2); font-size: 11px; font-weight: 600; .nr-input { width: 100%; min-width: 0; min-height: 38px; font-size: 12px; } }
.selected-track { display: flex; min-width: 0; align-items: center; gap: 6px; padding-top: 8px; border-top: 1px solid var(--nr-border); .track-order { display: grid; width: 21px; height: 21px; flex: 0 0 21px; place-items: center; color: var(--nr-accent-text); border-radius: 50%; background: var(--nr-accent-bg); font-size: 10px; } .nr-input { min-width: 0; min-height: 34px; flex: 1; padding-inline: 8px; font-size: 11px; } small { flex: 0 0 auto; color: var(--nr-text-3); font-size: 9px; white-space: nowrap; } button { display: grid; width: 27px; height: 29px; flex: 0 0 27px; place-items: center; color: var(--nr-text-3); border: 0; border-radius: 6px; background: transparent; cursor: pointer; &:hover:not(:disabled) { color: var(--nr-accent); background: var(--nr-accent-bg); } &:disabled { opacity: .3; cursor: default; } } .remove-track:hover { color: var(--nr-danger); background: var(--nr-danger-bg); } }
.audio-submit-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-top: 7px; color: var(--nr-text-3); font-size: 10px; }

@media (max-width: 850px) {
  .upload-container { grid-template-columns: 1fr; gap: 34px; padding-top: 42px; }
  .upload-intro { position: static; }
  .format-list { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 768px) {
  .page-top-inner { width: min(100% - 28px, var(--nr-content-width)); min-height: 62px; }
  .mini-brand span { display: none; }
  .upload-container { width: min(100% - 28px, 1060px); padding: 34px 0 48px; }
  .upload-intro h1 { font-size: 32px; }
  .format-list { grid-template-columns: 1fr; margin-top: 25px; }
  .upload-box { padding: 23px 18px; border-radius: var(--nr-radius-lg); }
  .nr-radios { display: grid; grid-template-columns: 1fr; }
  .upload-area { min-height: 180px; }
  .url-input { align-items: stretch; }
}
</style>
